import { createContext, useContext, useEffect, useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import * as yup from "yup";
import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";
import { CalculateAmountResult, PayAirtimeBillModel, useBuyAirtime, useComputeAmount, usePayBill } from "./utility";
import { WalletType, TransactionType } from "../../enums";
import { YupShape } from "../../shared";

type UtilityContentProps = {
  form: UseFormReturn<PayAirtimeBillModel, any>;
  bill: PackageInfo;
  setBill: React.Dispatch<React.SetStateAction<PackageInfo>>;
  amountSummary?: CalculateAmountResult;
  submitForm: () => void;
  sending: boolean;
};
type PackageInfo = {
  logoUrl?: string;
  categoryName?: string;
  name?: string;
  subName?: string;
};
const UtilityContent = createContext({} as UtilityContentProps);
export const schema = yup
  .object<YupShape<PayAirtimeBillModel>>({
    customerId: yup.string().required(),
    packageId: yup.string().required(),
    amount: yup.number().required().moreThan(0),
  })
  .required();
export const UtilityContentProvider = ({ children }) => {
  const [bill, setBill] = useState({} as PackageInfo);
  const form = useForm<PayAirtimeBillModel>({
    defaultValues: {
      packageId: "",
      amount: "",
    },
    resolver: yupResolver(schema),
  });
  const { watch, setError } = form;
  const model = watch();
  const { mutate: payBill, isLoading: sendingBill } = usePayBill();
  const { mutate: buyAirtime, isLoading: sendingAirtime } = useBuyAirtime();
  const navigate = useNavigate();
  const submitForm = () => {
    if (model.paymentMethodId === WalletType.Voucher) {
      if (!model.voucherCode) {
        setError("voucherCode", { message: "Voucher code is required.", type: "required" });
        return;
      }
      if (!model.pin) {
        setError("pin", { message: "Voucher pin is required." });
        return;
      }
    }
    if (model.billTypeId === TransactionType.BillsPayment) {
      payBill(model, {
        onSuccess: () => {
          toast.success("Order uploaded successfully.");
          navigate("/orders/bills");
        },
      });
    } else {
      buyAirtime(model, {
        onSuccess: () => {
          toast.success("Order uploaded successfully.");
          navigate("/orders/bills");
        },
      });
    }
  };

  const { mutate: calculatedAmount, data: summary } = useComputeAmount();
  useEffect(() => {
    if (model.amount && parseFloat(model.amount) > 0 && model.packageId) {
      calculatedAmount({
        amount: model.amount,
        billTypeId: model.billTypeId,
        packageId: model.packageId,
      });
    }
  }, [model.amount]);
  return (
    <UtilityContent.Provider
      value={{
        form,
        bill,
        setBill,
        amountSummary: summary,
        submitForm,
        sending: sendingAirtime || sendingBill,
      }}
    >
      {children}
    </UtilityContent.Provider>
  );
};

export const useUtilityContent = () => useContext(UtilityContent);
